import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiGetBillingPlans,
  apiBillingPortal,
  type BillingPlansInfo,
} from '../api';
import PageHeader from '../components/PageHeader';
import { withPageIcon } from '../lib/uiIcons';

// Pricing & subscription live on the marketing site. The app is the working
// tool: it only shows the caller's current plan and hands off management to the
// Stripe Customer Portal (POST /api/billing/portal) or to the site for plans.
const SITE_URL = 'https://bandzontour.com';

/**
 * Pricing / Subscription page (Stripe, Phase 2 — simplified).
 *
 * No purchase happens here: the app shows the caller's current subscription
 * (plan + monthly AI quota) and links out — to the Stripe Customer Portal to
 * manage an active subscription, or to bandzontour.com to see the plans.
 * i18n EN (default) / FR / ES.
 */

// Capitalize a plan slug for display ('solo' -> 'Solo'). Plan names are the
// same across languages.
function planLabel(plan: string): string {
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

// Human date from a UTC ISO period-end (e.g. '2026-09-23T…'). Empty -> ''.
function periodEndLabel(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function PricingPage() {
  const { t } = useTranslation();
  const [data, setData] = useState<BillingPlansInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await apiGetBillingPlans();
        if (cancelled) return;
        setData(d);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load pricing.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function openPortal() {
    setBusy(true);
    setError(null);
    try {
      const { url } = await apiBillingPortal();
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : t('billing.portalFailed'));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="page-pad">{t('common.loading')}</div>;
  if (error && !data) return <div className="page-pad">{t('billing.loadError')}: {error}</div>;

  const sub = data?.subscription || null;
  const hasActiveSub = !!sub && (sub.status === 'active' || sub.status === 'past_due');
  // The tier matching the caller's current plan — used to show its monthly AI quota.
  const currentTier = hasActiveSub ? (data?.tiers || []).find((t) => t.id === sub!.plan) : null;

  return (
    <div className="page-pad" data-page="pricing">
      <PageHeader
        title={withPageIcon('billing.title', t('billing.title'))}
        subtitle={
          hasActiveSub
            ? t('billing.manageHint', { plan: planLabel(sub!.plan) })
            : t('billing.subtitle')
        }
      />

      {!data?.configured && (
        <p style={{ color: 'var(--text-dim)' }}>{t('billing.notConfigured')}</p>
      )}
      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}

      {hasActiveSub ? (
        <div
          style={{
            display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap',
            padding: 14, borderRadius: 10, border: '1px solid var(--border)',
            maxWidth: 560, background: 'var(--surface)',
          }}
        >
          <div style={{ flex: '1 1 260px' }}>
            <strong>{t('billing.currentPlan', { plan: planLabel(sub!.plan) })}</strong>
            <div style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>
              {sub!.status === 'past_due'
                ? t('billing.statusPastDue')
                : t('billing.until', { date: periodEndLabel(sub!.current_period_end) })}
              {sub!.launch_price ? ` · ${t('billing.launchLocked')}` : ''}
            </div>
            {currentTier && (
              <div style={{ color: 'var(--text-dim)', fontSize: '0.9rem', marginTop: 2 }}>
                {t('billing.currentQuota', { n: currentTier.allowance })}
              </div>
            )}
          </div>
          <button className="btn btn-primary" onClick={openPortal} disabled={busy}>
            {busy ? t('billing.redirecting') : t('billing.manageSubscription')}
          </button>
        </div>
      ) : (
        <div
          style={{
            padding: 14, borderRadius: 10, border: '1px solid var(--border)',
            maxWidth: 560, background: 'var(--surface)',
          }}
        >
          <p style={{ marginTop: 0, color: 'var(--text-dim)' }}>{t('billing.onSiteHint')}</p>
          <a
            className="btn btn-primary"
            href={SITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', display: 'inline-block' }}
          >
            {t('billing.viewOnSite')} →
          </a>
        </div>
      )}
    </div>
  );
}
